import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Printer, FileText, ShieldCheck, Ban, Fuel, Truck } from 'lucide-react';
import { PageShell, primaryButton, ghostButton, dangerButton, inputStyle, glassLight, T, Badge, StatCard, EmptyState, Field, SectionTitle, usePagination, Pagination, useIsMobile } from '../ui';
import { COMPARTIMIENTOS, DESTINATARIO, PRODUCTOS_WOP, fmtFecha, fmtGal, imprimirPedido, nuevoToken } from './pedidoDocumento';
import type { Pedido, PedidoLinea, ProductoWOP } from '../types';
const hoyISO = () => new Date().toISOString().split('T')[0];
const totalGalones = (p: Pedido) => p.lineas.reduce((sum, l) => sum + (l.galones || 0), 0);
const estadoStyle = (estado: Pedido['estado']) => {
  if (estado === 'autorizado') return {
    color: T.ok,
    soft: T.okSoft,
    label: 'Autorizado'
  };
  if (estado === 'anulado') return {
    color: T.danger,
    soft: T.dangerSoft,
    label: 'Anulado'
  };
  return {
    color: T.warn,
    soft: T.warnSoft,
    label: 'Borrador'
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};

// Línea vacía para un compartimiento que todavía no se llenó.
const lineaVacia = (compartimiento: number): PedidoLinea => ({
  compartimiento,
  producto: 'gasoilRegular',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const lineaGLPVacia = (): PedidoLinea => ({
  compartimiento: 1,
  producto: 'glp',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const pedidoVacio = (): Partial<Pedido> => ({
  tipo: 'GLP',
  numeroOrden: '',
  fecha: hoyISO(),
  conductor: '',
  paseChofer: '',
  fichaCola: '',
  placaCamion: '',
  lineas: [lineaGLPVacia()],
  estado: 'borrador',
  observaciones: ''
});
interface PedidosProps {
  pedidos: Pedido[];
  onAdd: (p: Pedido) => void;
  onUpdate: (p: Pedido) => void;
  onDelete: (id: string) => void;
  // Permisos resueltos por el rol del usuario (ver App.tsx).
  canEdit: boolean;
  canArchive: boolean;
  canAutorizar: boolean;
  // Quién firma cuando se autoriza una orden.
  usuarioNombre: string;
  usuarioCargo: string;
  onLog?: (mensaje: string) => void;
}
export function Pedidos({
  pedidos,
  onAdd,
  onUpdate,
  onDelete,
  canEdit,
  canArchive,
  canAutorizar,
  usuarioNombre,
  usuarioCargo,
  onLog
}: PedidosProps) {
  const isMobile = useIsMobile();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<'todos' | 'WOP' | 'GLP'>('todos');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'borrador' | 'autorizado' | 'anulado'>('todos');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Pedido>>(pedidoVacio());
  const lineas = form.lineas ?? [];

  // ── FILTRADO ──────────────────────────────────────────────────────────
  const filtrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return pedidos.filter(p => filterTipo === 'todos' || p.tipo === filterTipo).filter(p => filterEstado === 'todos' || p.estado === filterEstado).filter(p => {
      if (!q) return true;
      const enDestinos = p.lineas.some(l => l.destino.toLowerCase().includes(q) || l.rncDestino.toLowerCase().includes(q));
      return p.numeroOrden.toLowerCase().includes(q) || p.conductor.toLowerCase().includes(q) || p.placaCamion.toLowerCase().includes(q) || enDestinos;
    }).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [pedidos, searchTerm, filterTipo, filterEstado]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados, 6);

  // ── ESTADÍSTICAS ──────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const vivos = pedidos.filter(p => p.estado !== 'anulado');
    const mes = hoyISO().slice(0, 7);
    return {
      total: pedidos.length,
      borradores: pedidos.filter(p => p.estado === 'borrador').length,
      autorizados: pedidos.filter(p => p.estado === 'autorizado').length,
      galonesMes: vivos.filter(p => (p.fecha || '').startsWith(mes)).reduce((s, p) => s + totalGalones(p), 0)
    };
  }, [pedidos]);

  // ── FORMULARIO ────────────────────────────────────────────────────────
  const abrirNuevo = () => {
    setForm(pedidoVacio());
    setEditingId(null);
    setShowForm(true);
  };
  const abrirEdicion = (p: Pedido) => {
    setForm({
      ...p,
      lineas: p.lineas.map(l => ({
        ...l
      }))
    });
    setEditingId(p.id);
    setShowForm(true);
  };
  const cerrarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(pedidoVacio());
  };

  // Cambiar de formato reinicia el detalle: las líneas de WOP (6
  // compartimientos con producto) y las de GLP (una sola, sin producto) no
  // son intercambiables.
  const cambiarTipo = (tipo: Pedido['tipo']) => {
    setForm(prev => ({
      ...prev,
      tipo,
      lineas: tipo === 'WOP' ? COMPARTIMIENTOS.map(lineaVacia) : [lineaGLPVacia()]
    }));
  };
  const setLinea = (index: number, patch: Partial<PedidoLinea>) => {
    setForm(prev => ({
      ...prev,
      lineas: (prev.lineas ?? []).map((l, i) => i === index ? {
        ...l,
        ...patch
      } : l)
    }));
  };
  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.numeroOrden?.trim()) {
      alert('Indica el número de orden de pedido.');
      return;
    }
    if (!form.fecha) {
      alert('Indica la fecha del pedido.');
      return;
    }
    if (!form.conductor?.trim() || !form.placaCamion?.trim()) {
      alert('Indica el conductor y la placa del camión.');
      return;
    }
    // Solo se guardan las líneas con galones: en WOP es normal dejar
    // compartimientos vacíos, y esos no deben imprimirse.
    const lineasUtiles = (form.lineas ?? []).filter(l => (l.galones || 0) > 0);
    if (lineasUtiles.length === 0) {
      alert('Registra al menos un compartimiento con galones.');
      return;
    }
    const sinDestino = lineasUtiles.some(l => !l.destino.trim());
    if (sinDestino) {
      alert('Cada compartimiento con galones necesita su destino.');
      return;
    }
    const base = {
      ...form,
      lineas: lineasUtiles
    } as Pedido;
    if (editingId) {
      onUpdate({
        ...base,
        id: editingId
      });
      onLog?.(`Orden de pedido editada: ${base.numeroOrden} (${base.tipo})`);
    } else {
      onAdd({
        ...base,
        id: Date.now().toString(),
        estado: 'borrador',
        autorizadoPor: '',
        autorizadoCargo: '',
        autorizadoFecha: '',
        // El token se acuña al autorizar, no al crear: un borrador todavía
        // no es un documento verificable.
        token: '',
        fecha_creacion: hoyISO()
      });
      onLog?.(`Orden de pedido creada: ${base.numeroOrden} (${base.tipo})`);
    }
    cerrarForm();
  };

  // ── AUTORIZACIÓN ──────────────────────────────────────────────────────
  // Autorizar equivale a firmar el formulario: se estampa quién lo hizo y
  // desde ahí el pedido ya no se edita.
  const autorizar = (p: Pedido) => {
    if (!window.confirm(`Vas a autorizar la orden ${p.numeroOrden} por ${fmtGal(totalGalones(p))} galones. Después de autorizarla no se podrá editar. ¿Continuar?`)) return;
    onUpdate({
      ...p,
      estado: 'autorizado',
      autorizadoPor: usuarioNombre,
      autorizadoCargo: usuarioCargo,
      autorizadoFecha: hoyISO(),
      // Se acuña una sola vez: si la orden ya se autorizó antes (y se
      // reautoriza tras una corrección) el QR impreso sigue siendo válido.
      token: p.token || nuevoToken()
    });
    onLog?.(`Orden de pedido autorizada: ${p.numeroOrden} por ${usuarioNombre}`);
  };
  const anular = (p: Pedido) => {
    if (!window.confirm(`¿Anular la orden ${p.numeroOrden}?`)) return;
    onUpdate({
      ...p,
      estado: 'anulado'
    });
    onLog?.(`Orden de pedido anulada: ${p.numeroOrden}`);
  };
  const eliminar = (p: Pedido) => {
    onDelete(p.id);
    onLog?.(`Orden de pedido eliminada: ${p.numeroOrden}`);
    setConfirmDelete(null);
  };

  // ── IMPRESIÓN ─────────────────────────────────────────────────────────
  const imprimir = (p: Pedido) => {
    imprimirPedido(p);
    onLog?.(`Orden de pedido impresa: ${p.numeroOrden}`);
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return <PageShell title="Órdenes de Pedido" sub={`Formulario de orden de pedido / retiro bajo contrato — ${DESTINATARIO}`} action={canEdit ? <button onClick={abrirNuevo} style={primaryButton} data-id="element-729" data-mp="15bl232wgg0xo">
            <Plus size={15} data-id="element-730" data-mp="15c4mqrdhp3fe" /> Nueva orden
          </button> : undefined} data-id="element-728" data-mp="wa3obr6isbc5">

      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
      gap: 14,
      marginBottom: 26
    }} data-id="element-731" data-mp="2puotk0wk4ojm">

        <StatCard label="Órdenes" value={String(stats.total)} note="registradas" data-id="element-732" data-mp="2gsny99kvwckl" />
        <StatCard label="Borradores" value={String(stats.borradores)} note="sin autorizar" accent={T.warn} data-id="element-733" data-mp="2gusaqfwumjwh" />

        <StatCard label="Autorizadas" value={String(stats.autorizados)} note="listas para despacho" accent={T.ok} data-id="element-734" data-mp="38k3rvnkuyzkb" />

        <StatCard label="Galones del mes" value={fmtGal(stats.galonesMes)} note="AG solicitados" accent={T.info} data-id="element-735" data-mp="34i05w50kccks" />

      </div>

      {/* ── FILTROS ── */}
      <div style={{
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      marginBottom: 20
    }} data-id="element-736" data-mp="2zpp4exq6ciq4">

        <div style={{
        position: 'relative',
        flex: '1 1 220px'
      }} data-id="element-737" data-mp="2uqvzhtoye01k">

          <Search size={15} style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: T.textMuted
        }} data-id="element-738" data-mp="3sa74latrgvnd" />

          <input style={{
          ...inputStyle,
          paddingLeft: 34
        }} placeholder="Buscar por orden, conductor, placa o destino" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-739" data-mp="2guio2hss8jdr" />

        </div>
        <select style={{
        ...inputStyle,
        flex: '0 1 150px'
      }} value={filterTipo} onChange={e => setFilterTipo(e.target.value as typeof filterTipo)} data-id="element-740" data-mp="2lrr4odya8fcw">

          <option value="todos" data-id="element-741" data-mp="2zoxmo6b6ma12">Todos los formatos</option>
          <option value="GLP" data-id="element-742" data-mp="2ure9l9q803ap">GLP</option>
          <option value="WOP" data-id="element-743" data-mp="2uqcwkzpn96qc">WOP</option>
        </select>
        <select style={{
        ...inputStyle,
        flex: '0 1 160px'
      }} value={filterEstado} onChange={e => setFilterEstado(e.target.value as typeof filterEstado)} data-id="element-744" data-mp="39fsahf42thom">

          <option value="todos" data-id="element-745" data-mp="3ih08rx6leiip">Todos los estados</option>
          <option value="borrador" data-id="element-746" data-mp="3iffbw0wt2nho">Borrador</option>
          <option value="autorizado" data-id="element-747" data-mp="3djq8fmho0mg3">Autorizado</option>
          <option value="anulado" data-id="element-748" data-mp="z303zwxbyj8">Anulado</option>
        </select>
      </div>

      {/* ── FORMULARIO ── */}
      <AnimatePresence data-id="element-749" data-mp="5xxi4zn2hj2a">
        {showForm && <motion.form onSubmit={guardar} initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} style={{
        ...glassLight,
        padding: isMobile ? 18 : 24,
        marginBottom: 24,
        overflow: 'hidden'
      }} data-id="element-750" data-mp="5wus0txkjknf">

            <SectionTitle data-id="element-751" data-mp="3j4pi2mxoo8qt">
              {editingId ? 'Editar orden de pedido' : 'Nueva orden de pedido'}
            </SectionTitle>

            <Field label="Formato del formulario" data-id="element-752" data-mp="34gky7fmw9r6g">
              <div style={{
            display: 'flex',
            gap: 10
          }} data-id="element-753" data-mp="34dynx88mcxkj">

                {(['GLP', 'WOP'] as const).map(t => {
              const activo = form.tipo === t;
              return <button key={t} type="button" onClick={() => cambiarTipo(t)} style={{
                ...(activo ? primaryButton : ghostButton),
                flex: 1,
                justifyContent: 'center'
              }} data-id="element-754" data-mp="2vhhc02sfkvnq">

                      {t === 'GLP' ? <Fuel size={15} data-id="element-755" data-mp="116832ozpd90u" /> : <Truck size={15} data-id="element-756" data-mp="115q1dcu9u2lj" />}
                      {t === 'GLP' ? 'GLP' : 'WOP (derivados)'}
                    </button>;
            })}
              </div>
            </Field>

            <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 16
        }} data-id="element-757" data-mp="s7w49zoon4xb">

              <Field label="Orden de pedido número *" data-id="element-758" data-mp="1ouhin96bp0jj">
                <input style={inputStyle} value={form.numeroOrden ?? ''} onChange={e => setForm({
              ...form,
              numeroOrden: e.target.value
            })} placeholder="1587725" data-id="element-759" data-mp="1jz292tfclqsx" />

              </Field>
              <Field label="Fecha *" data-id="element-760" data-mp="1az6ro6v78zkg">
                <input type="date" style={inputStyle} value={form.fecha ?? ''} onChange={e => setForm({
              ...form,
              fecha: e.target.value
            })} data-id="element-761" data-mp="4hjpq5yxm9xr" />

              </Field>
              <Field label="Conductor del camión *" data-id="element-762" data-mp="2b625fakrc6xf">
                <input style={inputStyle} value={form.conductor ?? ''} onChange={e => setForm({
              ...form,
              conductor: e.target.value
            })} placeholder="Nombre completo" data-id="element-763" data-mp="2b3691qok5qb3" />

              </Field>
              <Field label="Pase del chofer" data-id="element-764" data-mp="2owffpjl4fxpx">
                <input style={inputStyle} value={form.paseChofer ?? ''} onChange={e => setForm({
              ...form,
              paseChofer: e.target.value
            })} placeholder="APP-87" data-id="element-765" data-mp="2oy8jmr4co3u8" />

              </Field>
              <Field label="Ficha de la cola" data-id="element-766" data-mp="2ywodv2yobt7y">
                <input style={inputStyle} value={form.fichaCola ?? ''} onChange={e => setForm({
              ...form,
              fichaCola: e.target.value
            })} placeholder="33-21" data-id="element-767" data-mp="33togt5088fuu" />

              </Field>
              <Field label="Placa del camión *" data-id="element-768" data-mp="19hhx1ht9oqqx">
                <input style={inputStyle} value={form.placaCamion ?? ''} onChange={e => setForm({
              ...form,
              placaCamion: e.target.value
            })} placeholder="L-317355" data-id="element-769" data-mp="1dh8u239e033w" />

              </Field>
            </div>

            <SectionTitle data-id="element-770" data-mp="e4yzb699iwem">
              {form.tipo === 'WOP' ? 'Detalle por compartimiento' : 'Detalle del despacho'}
            </SectionTitle>

            <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginBottom: 18
        }} data-id="element-771" data-mp="n6f6740vcge3">

              {lineas.map((linea, i) => <div key={i} style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : form.tipo === 'WOP' ? '96px 1.3fr 100px 1.4fr 1fr' : '120px 1.6fr 1fr',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd
          }} data-id="element-772" data-mp="3vk5poevmqygx" data-mpi={i}>

                  {form.tipo === 'WOP' && <span style={{
              fontSize: 12,
              fontWeight: 700,
              color: T.text
            }} data-id="element-773" data-mp="37wxi1qaqbdbq">

                      Compart. {linea.compartimiento}
                    </span>}
                  {form.tipo === 'WOP' && <select style={inputStyle} value={linea.producto} onChange={e => setLinea(i, {
              producto: e.target.value as ProductoWOP
            })} data-id="element-774" data-mp="1s63mcoyfajzz">

                      {PRODUCTOS_WOP.map(prod => <option key={prod.id} value={prod.id} data-id="element-775" data-mp="1ig1z1zdw95m4">
                          {prod.label}
                        </option>)}
                    </select>}
                  <input type="number" min={0} style={inputStyle} value={linea.galones || ''} onChange={e => setLinea(i, {
              galones: Number(e.target.value) || 0
            })} placeholder="Galones" data-id="element-776" data-mp="2b4pq6blbqp7l" />

                  <input style={inputStyle} value={linea.destino} onChange={e => setLinea(i, {
              destino: e.target.value
            })} placeholder="Destino" data-id="element-777" data-mp="222girulcrbyf" />

                  <input style={inputStyle} value={linea.rncDestino} onChange={e => setLinea(i, {
              rncDestino: e.target.value
            })} placeholder="RNC del destino" data-id="element-778" data-mp="qovv17v1ynrk" />

                </div>)}
            </div>

            {form.tipo === 'WOP' && <div style={{
          fontSize: 11,
          color: T.textMuted,
          marginBottom: 16,
          marginTop: -8
        }} data-id="element-779" data-mp="2nf78kzr9079a">

                Los compartimientos sin galones no se imprimen en el formulario.
              </div>}

            <Field label="Observaciones" data-id="element-780" data-mp="29nk2k87i7kdb">
              <input style={inputStyle} value={form.observaciones ?? ''} onChange={e => setForm({
            ...form,
            observaciones: e.target.value
          })} placeholder="Opcional" data-id="element-781" data-mp="29olm712l5f6n" />

            </Field>

            <div style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end'
        }} data-id="element-782" data-mp="1zqo1gnjxq0is">

              <button type="button" onClick={cerrarForm} style={ghostButton} data-id="element-783" data-mp="3u2lyzs8vt7yu">
                Cancelar
              </button>
              <button type="submit" style={primaryButton} data-id="element-784" data-mp="3q2bukk0qmpm3">
                {editingId ? 'Guardar cambios' : 'Crear orden'}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>

      {/* ── LISTADO ── */}
      {filtrados.length === 0 ? <EmptyState title="Sin órdenes de pedido" hint={canEdit ? 'Crea la primera orden para imprimir el formulario de Refidomsa.' : 'Tu rol no permite crear órdenes de pedido.'} icon={<FileText size={22} data-id="element-786" data-mp="1pn0js6rcm0li" />} data-id="element-785" data-mp="1ljaxjny5bv3n" /> : <>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }} data-id="element-787" data-mp="sv6pwww0l7yb">

            {pageItems.map(p => {
          const es = estadoStyle(p.estado);
          const bloqueado = p.estado !== 'borrador';
          return <motion.div key={p.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} style={{
            ...glassLight,
            padding: isMobile ? 16 : '18px 20px',
            display: 'flex',
            gap: 16,
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap'
          }} data-id="element-788" data-mp="11u0kyitp5q9r">

                  <div style={{
              flex: '1 1 260px',
              minWidth: 0
            }} data-id="element-789" data-mp="1oyzqvyhia4ad">

                    <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                marginBottom: 6
              }} data-id="element-790" data-mp="22q4v7dvtjkr1">

                      <span style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: T.text
                }} data-id="element-791" data-mp="2clobirxwgauo">

                        Orden {p.numeroOrden || '—'}
                      </span>
                      <Badge label={p.tipo} color={p.tipo === 'GLP' ? T.brand : T.info} soft={p.tipo === 'GLP' ? T.accentSoft : T.infoSoft} dot={false} data-id="element-792" data-mp="2hmvjos3yfc1v" />

                      <Badge label={es.label} color={es.color} soft={es.soft} data-id="element-793" data-mp="raoi05je5ax7" />
                    </div>
                    <div style={{
                fontSize: 12,
                color: T.textSecondary
              }} data-id="element-794" data-mp="nae3u5lg3e79">

                      {fmtFecha(p.fecha)} · {p.conductor || 'Sin conductor'} ·{' '}
                      {p.placaCamion || 'Sin placa'}
                    </div>
                    <div style={{
                fontSize: 12,
                color: T.textMuted,
                marginTop: 3
              }} data-id="element-795" data-mp="3e1mdr6o7ouh">

                      {p.lineas.length}{' '}
                      {p.lineas.length === 1 ? 'destino' : 'destinos'} ·{' '}
                      {fmtGal(totalGalones(p))} galones
                      {p.estado === 'autorizado' && p.autorizadoPor ? ` · Autorizó ${p.autorizadoPor}` : ''}
                    </div>
                  </div>

                  <div style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap'
            }} data-id="element-796" data-mp="3llhbrvpvanb4">

                    <button type="button" onClick={() => imprimir(p)} style={iconBtn} title="Imprimir formulario" data-id="element-797" data-mp="1jst67fpr3ns6">

                      <Printer size={14} data-id="element-798" data-mp="2nczdvwvnexuh" />
                    </button>

                    {canAutorizar && p.estado === 'borrador' && <button type="button" onClick={() => autorizar(p)} style={{
                ...iconBtn,
                borderColor: `${T.ok}55`,
                color: T.ok
              }} title="Autorizar orden" data-id="element-799" data-mp="2s4r8qjlu7pqf">

                        <ShieldCheck size={14} data-id="element-800" data-mp="2iefr5u98di12" />
                      </button>}

                    {canEdit && !bloqueado && <button type="button" onClick={() => abrirEdicion(p)} style={iconBtn} title="Editar" data-id="element-801" data-mp="361nw2b2qyp2k">

                        <Edit2 size={14} data-id="element-802" data-mp="3b0hd2dh8sy7h" />
                      </button>}

                    {canArchive && p.estado === 'autorizado' && <button type="button" onClick={() => anular(p)} style={{
                ...iconBtn,
                borderColor: `${T.warn}55`,
                color: T.warn
              }} title="Anular orden" data-id="element-803" data-mp="314dz8ad13b4p">

                        <Ban size={14} data-id="element-804" data-mp="1ppqy8iibwj0e" />
                      </button>}

                    {canArchive && p.estado !== 'autorizado' && <button type="button" onClick={() => setConfirmDelete(p.id)} style={{
                ...iconBtn,
                borderColor: `${T.danger}55`,
                color: T.danger
              }} title="Eliminar" data-id="element-805" data-mp="1i4ohf7rdxw2a">

                        <Trash2 size={14} data-id="element-806" data-mp="1s22vj0pu68rf" />
                      </button>}
                  </div>

                  {confirmDelete === p.id && <div style={{
              flex: '1 1 100%',
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
              paddingTop: 12,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-807" data-mp="zf8g4i7vlzkt">

                      <span style={{
                fontSize: 12,
                color: T.textSecondary,
                flex: 1
              }} data-id="element-808" data-mp="1974hc259e5t9">

                        ¿Eliminar la orden {p.numeroOrden}? Esta acción no se
                        puede deshacer.
                      </span>
                      <button type="button" onClick={() => setConfirmDelete(null)} style={ghostButton} data-id="element-809" data-mp="bo2ypymaez8e">

                        Cancelar
                      </button>
                      <button type="button" onClick={() => eliminar(p)} style={dangerButton} data-id="element-810" data-mp="15e3h45209122">

                        Eliminar
                      </button>
                    </div>}
                </motion.div>;
        })}
          </div>

          <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="órdenes" data-id="element-811" data-mp="if6xke1ier2e" />

        </>}
    </PageShell>;
}